import { render } from 'engine/index';
import type { PersonalityColor, TeamKleurenState } from 'shared/modelTypes/team-kleuren';
import { rpc } from 'src/backend';
import { Table, Td, Th, TrBody, TrHead } from './TableComponents';
import { Button } from './Button';
import { CheckboxField, ToggleField } from './FormComponents';

export function PlayTeamKleuren({ licenseCode }: { licenseCode: string }) {
	const view: HTMLElement = <div>Will play game {licenseCode}</div>;

	const sessionId = licenseCode;

	rpc.onAdminUpdates.subscribe(
		{ sessionId },
		{
			onData(data) {
				//
				if (data.kind === 'update-state') {
					const state = data.state as TeamKleurenState;
					renderStage(state);
				}
			},
		},
	);

	const renderStage = (state: TeamKleurenState) => {
		const s = state.stage;
		switch (s) {
			case 'starting':
				render(view, <div class="animate-pulse text-xl text-gray-700">Starting play session</div>);
				break;
			case 'register-players':
				render(
					view,
					<PlayerRegisterView
						onContinue={() => {
							console.log('start the game');
							rpc.sendTeamKleurenMessage.query({ sessionId, update: { kind: 'start-game' } });
						}}
						state={state}
					/>,
				);
				break;
			case 'play-rounds':
				render(
					view,
					<div>
						<h3>Playing round {(state.roundIndex ?? 0) + 1}</h3> <p>let players make their moves.</p>
						<hr class="dark:border-gray-900" />
						<div class="mt-2 w-fit rounded-md border border-theme-300 bg-theme-100 p-3 text-theme-950 dark:border-theme-800 dark:bg-theme-900 dark:text-theme-200">
							{(() => {
								switch (state.currentGameColor) {
									case undefined:
										return (
											<span>
												<strong>{state.players[state.currentPlayerIndex].name}</strong> is picking landing spot
											</span>
										);
									case 'yellow':
										return <span>playing positive card quiz (yellow)</span>;
									case 'red':
										return <span>playing negative card quiz (red)</span>;
									case 'green':
										return <span>playing color quiz (green)</span>;
									case 'blue':
										return <span>playing text quiz (blue)</span>;
									case 'purple':
										return <span>playing random event (purple)</span>;
									case 'cyan':
										return <span>playing steal game (start square)</span>;
								}
							})()}
						</div>
						<PlayerTable state={state} sessionId={sessionId} />
						<div class="flex h-80 gap-10 overflow-auto">
							<div class="flex flex-col gap-1">
								<strong>Goede dag</strong>
								{state.goodCardPile.map((c) => (
									<span>{c.name}</span>
								))}
							</div>
							<div class="flex flex-col gap-1">
								<strong>Slechte dag</strong>
								{state.badCardPile.map((c) => (
									<span>{c.name}</span>
								))}
							</div>
							<div class="flex flex-col gap-1">
								<strong>Color questions</strong>
								{state.colorQuizPile.map((c) => (
									<span>{c.question}</span>
								))}
							</div>
							<div class="flex flex-col gap-1">
								<strong>Text questions</strong>
								{state.textQuiPile.map((c) => (
									<span>{c.question}</span>
								))}
							</div>
						</div>
						<div class="size-8"></div>
						<ToggleField
							compact
							name="lastRound"
							label="Laatste ronde"
							value={state.lastRound}
							onChange={(v) => {
								const lastRound = v || false;
								rpc.sendTeamKleurenMessage.query({ sessionId, update: { kind: 'set-is-last-round', lastRound } });
							}}
						/>
						<div class="size-8"></div>
						<Button
							on:click={() => {
								rpc.sendTeamKleurenMessage.query({ sessionId, update: { kind: 'force-restart-round' } });
							}}
						>
							Forceer nieuwe ronde start
						</Button>
					</div>,
				);
				break;
			case 'play-ending-game':
				{
					// const reports = generatePlayerReports();
					render(
						view,
						<div>
							playing Ending game.
							<PlayerTable state={state} sessionId={sessionId} />
							{/* <pre>{JSON.stringify(reports, null, 2)}</pre> */}
						</div>,
					);
				}

				break;
			case 'ending-report':
				render(
					view,
					<div>
						showing end report.
						<PlayerTable state={state} sessionId={sessionId} />
					</div>,
				);
				break;
		}
	};

	// render initial state
	(async () => {
		const state = await rpc.getGameState.query({ sessionId });
		renderStage(state);
	})();

	return view;
}

function PlayerRegisterView({ state, onContinue }: { state: TeamKleurenState; onContinue: () => void }) {
	console.log('render players', state.players);

	const playersWithName = state.players.filter((p) => p.name);

	return (
		<div>
			<h3 class="text-lg font-bold text-gray-800 dark:text-gray-200">Spelers registreren</h3>
			<p class="mb-5 text-gray-700">
				Laat spelers de QR code op het bord scannen en vervolgens hun gegevens invullen.
				<br />
				Druk op <strong>doorgaan</strong> als alle spelers gereed zijn.
			</p>
			{playersWithName.length > 0 && (
				<Table>
					<thead>
						<TrHead>
							<Th>Name</Th>
							<Th>Color</Th>
							<Th>Id</Th>
						</TrHead>
					</thead>
					<tbody>
						{playersWithName.map((player) => {
							return (
								<TrBody>
									<Td>{player.name}</Td>
									<Td>{player.color}</Td>
									<Td>{player.id}</Td>
								</TrBody>
							);
						})}
					</tbody>
				</Table>
			)}
			{!playersWithName.length && <small class="text-gray-700">Er zijn nog geen spelers.</small>}
			<div class="mt-5"></div>
			<Button on:click={onContinue}>Doorgaan</Button>
		</div>
	);
}

function Card({ name, color }: { name: string; color: PersonalityColor }) {
	let borderColor = 'border-gray-200';
	switch (color) {
		case 'blue':
			borderColor = 'border-blue-400';
			break;
		case 'red':
			borderColor = 'border-red-400';
			break;
		case 'yellow':
			borderColor = 'border-yellow-300';
			break;
		case 'green':
			borderColor = 'border-green-400';
			break;
	}
	return <div class={'mb-1 rounded-md border bg-white p-1 text-sm text-gray-800 shadow-sm ' + borderColor}>{name}</div>;
}

function PlayerTable({ sessionId, state }: { sessionId: string; state: TeamKleurenState }) {
	return (
		<>
			<h3 class="mb-2 mt-5 text-lg font-bold text-gray-900 dark:text-gray-200">Players:</h3>
			<Table>
				<thead>
					<TrHead>
						<Th title="When disabled the players turn will always be skipped">Active</Th>
						<Th>Name</Th>
						<Th>Color</Th>
						<Th>Score</Th>
						<Th>Good Cards</Th>
						<Th>Bad Cards</Th>
						<Th>Id</Th>
					</TrHead>
				</thead>
				<tbody>
					{state.players.map((player) => {
						return (
							<TrBody>
								<Td>
									<CheckboxField
										name={player.id + '-active'}
										label={''}
										value={player.active}
										onChange={(v) => {
											rpc.sendTeamKleurenMessage.query({
												sessionId,
												update: { kind: 'set-player-active', playerId: player.id, active: v ?? true },
											});
										}}
									/>
								</Td>
								<Td>{player.name}</Td>
								<Td>{player.color}</Td>
								<Td>{player.score}</Td>

								<Td>
									{player.goodCards.map((c) => (
										<Card {...c} />
									))}
								</Td>
								<Td>
									{player.badCards.map((c) => (
										<Card {...c} />
									))}
								</Td>
								<Td>{player.id}</Td>
							</TrBody>
						);
					})}

					<TrBody>
						<Td></Td>
						<Td>
							<strong>Totaal:</strong>
						</Td>
						<Td>{state.players.reduce((acc, cur) => cur.score + acc, 0)}</Td>

						<Td></Td>
						<Td></Td>
						<Td></Td>
					</TrBody>
				</tbody>
			</Table>
		</>
	);
}
